import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { Input, Text } from '@Sage/hoi-poi-ui';
import ModalHoi from 'components/ModalHoi';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const SaveAsTemplateModal = ({
    isOpen,
    isSubmitting = false,
    defaultName = '',
    onCancel,
    onConfirm,
    onRequestClose,
}) => {
    const [templateName, setTemplateName] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setTemplateName('');
            return;
        }

        setTemplateName(typeof defaultName === 'string' ? defaultName : '');
    }, [defaultName, isOpen]);

    const handleClose = useCallback(() => {
        if (isSubmitting) {
            return;
        }
        onCancel?.();
    }, [isSubmitting, onCancel]);

    const isConfirmDisabled = useMemo(
        () => !templateName.trim() || isSubmitting,
        [isSubmitting, templateName],
    );

    const handleConfirm = useCallback(() => {
        const trimmedName = templateName.trim();
        if (!trimmedName || isSubmitting) {
            return;
        }
        onConfirm?.(trimmedName);
    }, [isSubmitting, onConfirm, templateName]);

    const handleTemplateNameChange = useCallback((value) => {
        setTemplateName(value);
    }, []);

    return (
        <ModalHoi
            className="save-as-template-modal"
            isOpen={isOpen}
            onRequestClose={onRequestClose ?? handleClose}
            onCancel={handleClose}
            onConfirm={handleConfirm}
            title={getLiteral('label_marketing_campaigns_modal_save_as_template_title')}
            cancelText={getLiteral('action_cancel')}
            confirmText={getLiteral('action_save')}
            isConfirmLoading={isSubmitting}
            isConfirmDisabled={isConfirmDisabled}
            useHeader
            useCornerClose
            size="medium"
        >
            <div className="save-as-template-modal__content">
                <Text type="body" className="save-as-template-modal__description">
                    {getLiteral('label_marketing_campaigns_modal_save_as_template_desc')}
                </Text>
                <Input
                    label={getLiteral('label_name')}
                    placeholder={getLiteral('placeholder_marketing_campaigns_enter_template_name')}
                    value={templateName}
                    onChange={handleTemplateNameChange}
                    isRequired
                    isFullWidth
                    autoFocus
                />
            </div>
        </ModalHoi>
    );
};

SaveAsTemplateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    isSubmitting: PropTypes.bool,
    defaultName: PropTypes.string,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    onRequestClose: PropTypes.func,
};

SaveAsTemplateModal.displayName = 'SaveAsTemplateModal';

export default memo(SaveAsTemplateModal);
